import { type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import { motion } from 'framer-motion'

interface ProtectedRouteProps {
  children: ReactNode
  requiredRoles?: string[]
  redirectTo?: string
}

/**
 * Wrapper component that protects routes from unauthorized access.
 * Redirects to login if not authenticated, or shows forbidden if lacking role.
 */
export function ProtectedRoute({
  children,
  requiredRoles,
  redirectTo = '/login',
}: ProtectedRouteProps) {
  const { user, isAuthenticated, isLoading, token } = useAuthStore()
  const location = useLocation()

  // Show loading state while checking auth
  if (isLoading || (token && !isAuthenticated)) {
    return <AuthLoadingScreen />
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location.pathname }}
        replace
      />
    )
  }

  // Check role requirements
  if (requiredRoles && requiredRoles.length > 0 && user) {
    if (!requiredRoles.includes(user.role)) {
      return <ForbiddenScreen />
    }
  }

  return <>{children}</>
}

/**
 * Loading screen shown while checking authentication status
 */
function AuthLoadingScreen() {
  return (
    <div className="min-h-screen bg-primary-deep flex items-center justify-center">
      <motion.div
        className="flex flex-col items-center gap-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <motion.div
          className="w-12 h-12 rounded-full border-2 border-primary-accent border-t-transparent"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        />
        <p className="text-neutral-400 text-sm">Verifying authentication...</p>
      </motion.div>
    </div>
  )
}

/**
 * Forbidden screen shown when user lacks required role
 */
function ForbiddenScreen() {
  return (
    <div className="min-h-screen bg-primary-deep flex items-center justify-center">
      <motion.div
        className="text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="text-6xl font-heading font-bold text-secondary-pink mb-4">
          403
        </div>
        <h1 className="text-2xl font-heading font-bold text-neutral-100 mb-2">
          Access Denied
        </h1>
        <p className="text-neutral-400 mb-6">
          You don't have permission to access this page.
        </p>
        <a
          href="/"
          className="inline-flex items-center px-4 py-2 rounded-xl bg-primary-mid text-neutral-100 hover:bg-primary-mid/80 transition-colors"
        >
          Go Home
        </a>
      </motion.div>
    </div>
  )
}

/**
 * HOC version of ProtectedRoute for easier route configuration
 */
export function withAuth<P extends object>(
  Component: React.ComponentType<P>,
  options?: { requiredRoles?: string[]; redirectTo?: string }
) {
  return function AuthenticatedComponent(props: P) {
    return (
      <ProtectedRoute
        requiredRoles={options?.requiredRoles}
        redirectTo={options?.redirectTo}
      >
        <Component {...props} />
      </ProtectedRoute>
    )
  }
}
